import { DictionariesService, DictionaryItem } from "@sa/rep-mdm";
import { debounce, orderBy } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { debounceTime } from "rxjs/operators";
import { useHookSubscribe } from "../../react-rxjs";
import { ServicesMaps } from "../MapReportingProvider";
import { MapDataSourceId } from "../models/MapDataSource";
import { MapDimension } from "../models/MapDimension";
import { useMapStore } from "./useMapStore";
import { useService } from "./useService";
import { attributesFilter$ } from "../utils/attributesFilter$";
import { Subscription } from "rxjs";

export interface MapDimensionHookItem<T> {
  id: T;
  name: string;
  parent?: T;
  data: DictionaryItem;
}

export function useMapDimension<T = unknown>(dimension?: MapDimension) {
  const [items, setItems] = useState<MapDimensionHookItem<T>[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasValue, setHasValue] = useState<boolean>(true);
  const store = useMapStore();
  const streamValue$ = store.dimensionsValues.get(dimension?.id || -1)!;
  const value = useHookSubscribe(undefined, streamValue$);
  const dataSourceId = useHookSubscribe(MapDataSourceId.Default, store.dataSourceId$);

  const setValue = useCallback(
    (val: unknown) => {
      streamValue$ && streamValue$.next(val);
    },
    [streamValue$],
  );

  const dictionariesService = useService<DictionariesService>(ServicesMaps.Dictionaries);
  useEffect(() => {
    const subscribes: Subscription[] = [];
    if (dimension) {
      setLoading(true);
      if (!dimension.hierarchy.includes("attributes") && !dimension.hierarchy.includes("flow")) {
        fetchItems<T>(dimension, dictionariesService).then((nextItems) => {
          if (dimension.type === 3) {
            // TODO: Заменить на enum. Это тип календаря
            setItems(nextItems.filter((e) => e.data.values["hasData"]));
          } else {
            setItems(nextItems);
          }
          setLoading(false);
        });
      } else {
        const attributesFilterSubscription = attributesFilter$(store).subscribe(
          (parameters) => {
            fetchItems<T>(dimension, dictionariesService, parameters).then((nextItems) => {
              setItems(orderBy(nextItems, e => e.name));
              setLoading(false);
            });
          },
        );
        subscribes.push(attributesFilterSubscription);
      }
    }

    const valueSubscribe = streamValue$?.subscribe((val) => setHasValue(val !== null && val !== undefined));
    subscribes.push(valueSubscribe);

    return () => subscribes.forEach((s) => s?.unsubscribe());
  }, [dimension, dataSourceId, dictionariesService]);

  const result = useMemo(() => ({ value, items, loading: loading || !hasValue, setValue }), [
    value,
    items,
    loading,
    hasValue,
    setValue,
  ]);
  return result;
}

async function fetchItems<T>(
  dimension: MapDimension,
  dictionariesService: DictionariesService,
  parameters: Record<string, unknown> = {}
): Promise<MapDimensionHookItem<T>[]> {
  const dictionary = await dictionariesService.getDictionary({ id: dimension.identifier, parameters });
  const items = await dictionary.getAll();
  return items
    .filter((e) => e.values["hidden"] !== true)
    .map((e) => ({
      id: e.getValue<T>("id")!,
      name:
        (typeof e.getValue("name") === "object"
          ? (e.getValue<object>("name")?.[9] as string)
          : e.getValue<string>("name")) || "",
      parent: (e.values["parent"] || e.values["parent_id"]) as T | undefined,
      data: e,
    }));
}
